<script setup lang="ts">
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue';
import i18n from '@/i18n';
import ActionButton from '@/components/ui/ActionButton.vue';
import EmptyState from '@/components/ui/EmptyState.vue';
import RouteBadge from '@/components/ui/RouteBadge.vue';
import TabsBar, { type TabItem } from '@/components/ui/TabsBar.vue';
import { compareRoutes } from '@pysae/transit-core/helpers';
import { alertRouteIds, groupAlertsByEntity, type AlertGroups } from '@/libs/alerts-helper';
import AlertCard from './AlertCard.vue';
import type { Alert } from '@/@types/alert';
import type { Route, Stop } from '@/@types/gtfs-ressource';
import { useAlertsStore } from '@/stores/alerts';
import { useGtfsStore } from '@/stores/gtfs';

/**
 * Tab holding everything not tied to a known route: network-wide alerts and
 * stop-only alerts (shown with their stop name as context).
 * The sentinel shares the tab-id namespace with GTFS route ids (free-form
 * operator strings), hence a value no feed can carry.
 */
const NETWORK_TAB_ID = '__network__';

const t = i18n.global.t;

const alertsStore = useAlertsStore();
const gtfsStore = useGtfsStore();

const props = defineProps<{
  alertId?: string;
}>();

interface AlertCardItem {
  alert: Alert;
  /** Stop names of a stop-only alert, `undefined` for a route or network-wide one. */
  context?: string;
}

const openAlertIds = ref<Set<string>>(new Set());
const selectedTabId = ref<string>(NETWORK_TAB_ID);
const trackEl = ref<HTMLElement>();

const routes = computed<{ [routeId: string]: Route }>(() => gtfsStore.gtfsData.routes);
const stops = computed<{ [stopId: string]: Stop }>(() => gtfsStore.gtfsData.stops);

const alertGroups = computed<AlertGroups>(() =>
  groupAlertsByEntity(alertsStore.alerts, routes.value, stops.value),
);

const routeAlerts = computed<{ [routeId: string]: Array<Alert> }>(() => alertGroups.value.routes);

const networkAlerts = computed<Array<AlertCardItem>>(() =>
  alertGroups.value.network.map(({ alert, stopNames }) => ({
    alert,
    context: stopNames.length > 0 ? stopNames.join(', ') : undefined,
  })),
);

// Same ordering as the route list: route_sort_order first, alphanumeric fallback.
const alertedRoutes = computed<Array<Route>>(() =>
  Object.keys(routeAlerts.value)
    .map(routeId => routes.value[routeId])
    .sort(compareRoutes),
);

const tabs = computed<Array<TabItem>>(() => {
  const tabs: Array<TabItem> = [];
  if (networkAlerts.value.length > 0) tabs.push({ id: NETWORK_TAB_ID, label: t('alerts.network') });
  alertedRoutes.value.forEach(route => tabs.push({ id: route.route_id }));
  return tabs;
});

const cardsByTab = computed<{ [tabId: string]: Array<AlertCardItem> }>(() =>
  Object.entries(routeAlerts.value).reduce<{ [tabId: string]: Array<AlertCardItem> }>(
    (cards, [routeId, alerts]) => {
      cards[routeId] = alerts.map(alert => ({ alert }));
      return cards;
    },
    { [NETWORK_TAB_ID]: networkAlerts.value },
  ),
);

const hasAlert = computed<boolean>(() => alertsStore.alertCount > 0);

const activeIndex = computed<number>(() =>
  Math.max(
    0,
    tabs.value.findIndex(({ id }) => id === selectedTabId.value),
  ),
);

// Alerts refresh every 5 minutes: an expired alert may take its tab away from
// under the selection.
watch(
  tabs,
  tabList => {
    if (!tabList.some(tab => tab.id === selectedTabId.value)) {
      selectedTabId.value = tabList[0]?.id ?? NETWORK_TAB_ID;
    }
  },
  { immediate: true },
);

/**
 * What is unfolded is decided in one place: a deep link from a push notification,
 * otherwise the first alert of the tab. Two watchers would race — Vue flushes them
 * in an order that depends on their dependencies, not on their declaration.
 */
let handledAlertId: string | undefined;
const defaultedTabIds = new Set<string>();

function openAlert(alertId: string) {
  openAlertIds.value = new Set(openAlertIds.value).add(alertId);
}

watch(
  [() => props.alertId, () => alertsStore.alerts, routes, selectedTabId],
  async ([alertId]) => {
    // Handled once per alertId — the periodic refresh recreates the alerts array
    // and must not re-scroll the page under the passenger.
    const deepLinked =
      alertId && alertId !== handledAlertId
        ? alertsStore.alerts.find(alert => alert._id === alertId)
        : undefined;

    if (alertId && deepLinked) {
      // A line alert cannot resolve its tab before the GTFS routes are known —
      // alerts respond before the GTFS when a new gtfs_id forces an API reload.
      // Nothing unfolds meanwhile: the deep-linked alert is what was tapped.
      const needsRoutes = deepLinked.informed_entity?.some(entity => entity.route_id);
      if (needsRoutes && Object.keys(routes.value).length === 0) return;

      handledAlertId = alertId;
      const routeIds = alertRouteIds(deepLinked, routes.value);
      selectedTabId.value = routeIds[0] ?? NETWORK_TAB_ID;
      // Its tab does not also unfold its first alert: one card opens, not two.
      defaultedTabIds.add(selectedTabId.value);
      openAlert(alertId);

      await nextTick();
      scrollActivePaneTo(alertId);
      return;
    }

    // The first alert of a tab unfolds itself, so the view opens on something to
    // read rather than on a list of headlines. Once per tab only: the 5-minute
    // refresh must not reopen a card the passenger closed, and neither must
    // coming back to a tab. An alert with no description never opens, so
    // defaulting to one would leave the view closed.
    if (defaultedTabIds.has(selectedTabId.value)) return;
    const firstDetailed = cardsByTab.value[selectedTabId.value]?.find(({ alert }) => alert.description_text);
    if (!firstDetailed) return;

    defaultedTabIds.add(selectedTabId.value);
    openAlert(firstDetailed.alert._id);
  },
  { immediate: true },
);

/**
 * Every pane is mounted, so an alert on several lines exists once per pane and
 * `getElementById` could land in another tab's pane. `scrollIntoView` is out
 * too: the track is still sliding, and it would scroll the viewport sideways
 * to reach the card.
 */
function scrollActivePaneTo(alertId: string) {
  const pane = trackEl.value?.children[activeIndex.value];
  if (!(pane instanceof HTMLElement)) return;
  const card = pane.querySelector(`[id="${CSS.escape(alertId)}"]`);
  if (!(card instanceof HTMLElement)) return;
  pane.scrollTo({ top: card.offsetTop, behavior: 'smooth' });
}

onBeforeMount(() => {
  alertsStore.getAlerts();
});

function tabAlertCount(tabId: string): number {
  return cardsByTab.value[tabId]?.length ?? 0;
}

function setAlertOpen(alertId: string, open: boolean) {
  if (open) {
    openAlert(alertId);
    return;
  }
  const openIds = new Set(openAlertIds.value);
  openIds.delete(alertId);
  openAlertIds.value = openIds;
}
</script>

<template>
  <div class="page">
    <div class="content alerts-page">
      <h1 class="alerts-page__title">{{ $t('alerts.title') }}</h1>

      <!-- Alerts already shown stay on screen while a refresh loads or fails -->
      <template v-if="!hasAlert">
        <p
          v-if="alertsStore.state === 'loading'"
          class="alerts-page__status alerts-page__loading"
          role="status"
        >
          {{ $t('common.loading') }}
        </p>

        <!-- An error must never pass for "no disruption": the passenger would wrongly trust the network -->
        <EmptyState
          v-else-if="alertsStore.state === 'error'"
          class="alerts-page__status"
          icon="circle-alert"
          role="alert"
          :title="$t('alerts.error')"
        >
          <template #actions>
            <ActionButton
              :label="$t('common.retry')"
              leading-icon="rotate-ccw"
              type="outline"
              @click="alertsStore.retry()"
            />
          </template>
        </EmptyState>

        <EmptyState
          v-else
          class="alerts-page__status"
          icon="circle-check-big"
          :title="$t('alerts.empty.title')"
          :description="$t('alerts.empty.description')"
        />
      </template>

      <template v-else>
        <TabsBar v-model="selectedTabId" class="alerts-page__tabs" :tabs="tabs">
          <template #tab="{ tab }">
            <RouteBadge v-if="routes[tab.id]" :route="routes[tab.id]" size="small" />
            <span v-else class="tabs-bar__label">{{ tab.label }}</span>
            <span class="alerts-page__tab-count">{{ tabAlertCount(tab.id) }}</span>
          </template>
        </TabsBar>

        <!-- Every tab stays mounted and the panes slide as one track, so a tab
             switch swipes and each tab keeps its scroll position. -->
        <div class="alerts-page__viewport">
          <div
            ref="trackEl"
            class="alerts-page__track"
            :style="{ transform: `translateX(-${activeIndex * 100}%)` }"
          >
            <div
              v-for="tab in tabs"
              :key="tab.id"
              class="alerts-page__pane"
              :inert="tab.id !== selectedTabId"
            >
              <AlertCard
                v-for="{ alert, context } in cardsByTab[tab.id]"
                :key="alert._id"
                :alert="alert"
                :context="context"
                :open="openAlertIds.has(alert._id)"
                @update:open="setAlertOpen(alert._id, $event)"
              />
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
.alerts-page {
  background-color: $color-white;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__title {
    color: $color-neutral-highest;
    flex-shrink: 0;
    font: $heading-h1;
    letter-spacing: $heading-h1-spacing;
    margin: 0;
    padding: $spacing-5;
  }

  &__tabs {
    flex-shrink: 0;
  }

  &__viewport {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }

  &__track {
    display: flex;
    height: 100%;
    transition: transform $duration-drawer-open $easing-drawer;
    will-change: transform;
  }

  // Positioned so a card's offsetTop is measured from its pane
  &__pane {
    flex: 0 0 100%;
    overflow-y: auto;
    position: relative;
  }

  &__tab-count {
    color: $color-neutral-highest;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
  }

  &__status {
    flex: 1 1 auto;
  }

  &__loading {
    align-items: center;
    color: $color-text-neutral;
    display: flex;
    font: $label-medium;
    justify-content: center;
    letter-spacing: $label-medium-spacing;
    margin: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .alerts-page__track {
    transition: none;
  }
}
</style>
